import { FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import { useCreateIntakeQuestionMutation } from "src/api/services/intakeQuestionsApi";
import { IntakeQuestion, Question } from "src/api/types";
import { useSentryLogger } from "src/hooks/useSentryLogger";

import QuestionForm from "../QuestionForm";

interface Props {
  intakeId: number;
  onQuestionCreated?: () => void;
  currentQuestion: IntakeQuestion | undefined;
}

const QuestionBuilder = ({
  intakeId,
  onQuestionCreated,
  currentQuestion,
}: Props) => {
  const [createQuestion, { isLoading }] = useCreateIntakeQuestionMutation();
  const [nextQuestion, setNextQuestion] = useState<Question | null>(null);
  const [selectedQuestion, setSelectedQuestion] = useState<Question | null>(
    null,
  );
  const [position, setPosition] = useState<number>(0);
  const [error, setError] = useState<string>("");
  const logError = useSentryLogger("error");

  const nextPosition = useMemo(() => {
    if (currentQuestion == null) return 0;

    return (currentQuestion.question_position ?? 0) + 1;
  }, [currentQuestion]);

  const handleSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      if (selectedQuestion == null) {
        setError("Question is required");
        return;
      }

      setError("");

      try {
        await createQuestion({
          intakeId,
          body: {
            question_id: selectedQuestion.id,
            question_position: position,
            next_question: nextQuestion?.slug,
          },
        }).unwrap();

        setPosition(0);
        setSelectedQuestion(null);
        setNextQuestion(null);
        onQuestionCreated?.();
      } catch (e) {
        logError("Failed to create intake question", e);
        setError("Failed to create intake question");
      }
    },
    [
      createQuestion,
      intakeId,
      logError,
      nextQuestion?.slug,
      onQuestionCreated,
      position,
      selectedQuestion,
    ],
  );

  useEffect(() => {
    if (nextPosition != null) {
      setPosition(nextPosition);
    }
  }, [nextPosition]);

  return (
    <QuestionForm
      error={error}
      isLoading={isLoading}
      nextQuestion={nextQuestion}
      onSubmit={handleSubmit}
      position={position}
      setPosition={setPosition}
      selectedQuestion={selectedQuestion}
      setSelectedQuestion={setSelectedQuestion}
      setNextQuestion={setNextQuestion}
      setError={setError}
    />
  );
};

export default QuestionBuilder;
